import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import FlashInfo from '../FlashInfo';
import { createPaymentCheckout } from '../../utils/paymentUtils';
import { toast } from 'sonner';
import { 
  ShoppingBag, 
  ArrowLeft, 
  Smartphone, 
  Laptop, 
  Headphones,
  Wifi,
  Tag,
  TrendingDown,
  CreditCard
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromotionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [processingPayment, setProcessingPayment] = useState(null);

  useEffect(() => {
    // Check if there's a category filter in URL
    const categoryParam = searchParams.get('category');
    if (categoryParam === 'abonnements') {
      setSelectedCategory('abonnements');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const handleBuyProduct = async (product) => {
    setProcessingPayment(product.id);
    
    try {
      await createPaymentCheckout({
        payment_type: 'product',
        product_id: product.id,
        customer_name: 'Client',  // Will be updated to collect customer info
        customer_email: 'temp@email.com',  // Will be updated
        metadata: {
          product_name: product.name,
          product_category: product.category
        }
      });
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Erreur lors de l\'initialisation du paiement');
      setProcessingPayment(null);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url;
      if (selectedCategory === 'all') {
        url = `${API}/products`;
      } else if (selectedCategory === 'abonnements') {
        // Fetch all subscription products (VOO, DIGI, Hey)
        url = `${API}/products`;
      } else {
        url = `${API}/products?category=${selectedCategory}`;
      }
      
      const response = await axios.get(url);
      let filteredProducts = response.data.filter(p => p.is_active);
      
      // Filter for subscription categories if needed
      if (selectedCategory === 'abonnements') {
        filteredProducts = filteredProducts.filter(p => 
          p.category === 'abonnement_voo' || 
          p.category === 'abonnement_digi' || 
          p.category === 'abonnement_hey'
        );
      }
      
      setProducts(filteredProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'Tous', icon: ShoppingBag },
    { value: 'smartphone_neuf', label: 'Smartphones Neufs', icon: Smartphone },
    { value: 'smartphone_reconditionne', label: 'Reconditionnés', icon: Smartphone },
    { value: 'pc_portable', label: 'PC Portables', icon: Laptop },
    { value: 'accessoire', label: 'Accessoires', icon: Headphones },
    { value: 'abonnements', label: 'Abonnements', icon: Wifi },
    { value: 'abonnement_voo', label: 'VOO', icon: Wifi },
    { value: 'abonnement_digi', label: 'DIGI', icon: Wifi },
    { value: 'abonnement_hey', label: 'Hey Télécom', icon: Wifi }
  ];

  const isSubscriptionCategory = (category) => {
    return category === 'abonnement_voo' || 
           category === 'abonnement_digi' || 
           category === 'abonnement_hey';
  };

  const getCategoryColor = (category) => {
    const colors = {
      smartphone_neuf: 'bg-blue-100 text-blue-800',
      smartphone_reconditionne: 'bg-green-100 text-green-800',
      pc_portable: 'bg-purple-100 text-purple-800',
      accessoire: 'bg-orange-100 text-orange-800',
      abonnement_voo: 'bg-red-100 text-red-800',
      abonnement_digi: 'bg-cyan-100 text-cyan-800',
      abonnement_hey: 'bg-pink-100 text-pink-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Button>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Tag className="w-8 h-8 text-white" />
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {selectedCategory === 'abonnements' ? 'Abonnements Internet, TV & Mobile' : 'Promotions & Boutique'}
            </h1>
            <p className="text-gray-600">
              {selectedCategory === 'abonnements' 
                ? 'Comparez nos offres VOO, DIGI-BELGIUM et Hey Télécom' 
                : 'Découvrez nos meilleures offres sur smartphones, PC et abonnements'}
            </p>
          </div>
        </div>

        {/* Flash Info */}
        <FlashInfo pageName="promotions" />

        {/* Categories Filter */}
        <div className="mb-8 overflow-x-auto pb-4">
          <div className="flex gap-2 min-w-max">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <Button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  variant={selectedCategory === category.value ? 'default' : 'outline'}
                  className="whitespace-nowrap"
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {category.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des produits...</p>
          </div>
        ) : products.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Aucun produit disponible
              </h3>
              <p className="text-gray-600 mb-6">
                Les produits seront bientôt disponibles dans cette catégorie
              </p>
              <Button onClick={() => navigate('/public/contact')}>
                Nous Contacter
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card key={product.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                {/* Product Image */}
                {product.image_url ? (
                  <div className="h-48 bg-gray-200 overflow-hidden">
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                    <Smartphone className="w-16 h-16 text-blue-300" />
                  </div>
                )}

                <CardContent className="p-6">
                  {/* Category Badge */}
                  <Badge className={`${getCategoryColor(product.category)} mb-3`}>
                    {categories.find(c => c.value === product.category)?.label}
                  </Badge>

                  {/* Featured Badge */}
                  {product.is_featured && (
                    <Badge className="ml-2 bg-yellow-100 text-yellow-800">
                      ⭐ Coup de cœur
                    </Badge>
                  )}

                  {/* Product Name */}
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {product.name}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  {/* Price */}
                  <div className="flex items-center gap-2 mb-4">
                    {product.original_price && (
                      <span className="text-lg text-gray-400 line-through">
                        {product.original_price.toFixed(2)}€
                      </span>
                    )}
                    <span className="text-2xl font-bold text-blue-600">
                      {product.price.toFixed(2)}€
                    </span>
                    {product.original_price && (
                      <Badge className="bg-red-100 text-red-800">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        -{Math.round((1 - product.price / product.original_price) * 100)}%
                      </Badge>
                    )}
                  </div>

                  {/* Stock - Hidden for subscriptions */}
                  {!isSubscriptionCategory(product.category) && product.stock > 0 && (
                    <p className="text-xs text-green-600 mb-3">
                      ✓ En stock ({product.stock} disponible{product.stock > 1 ? 's' : ''})
                    </p>
                  )}
                  
                  {/* Subscription badge */}
                  {isSubscriptionCategory(product.category) && (
                    <p className="text-xs text-blue-600 mb-3 font-semibold">
                      📡 Abonnement disponible
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <Button 
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" 
                      onClick={() => handleBuyProduct(product)}
                      disabled={processingPayment === product.id || (!isSubscriptionCategory(product.category) && product.stock === 0)}
                    >
                      {processingPayment === product.id ? (
                        <>
                          <div className="spinner w-4 h-4 mr-2"></div>
                          Redirection...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Acheter maintenant
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      variant="outline"
                      className="w-full" 
                      onClick={() => navigate('/public/contact')}
                    >
                      Demander Informations
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <Card className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-700 text-white border-none">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">
              Vous ne trouvez pas ce que vous cherchez ?
            </h2>
            <p className="text-blue-100 mb-6">
              Contactez-nous pour des demandes spécifiques ou des produits sur commande
            </p>
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-blue-50"
              onClick={() => navigate('/public/contact')}
            >
              Nous Contacter
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PromotionsPage;
